import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Truck, Bug, Fence, Wrench, Flower2, Cog,
  Package, ScrollText, FileText, Upload, Tags, LogOut, FileBadge,
  Sprout, CalendarRange, Users
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuthStore } from "@/lib/auth";
import { logout } from "@/lib/auth";
import { cn } from "@/lib/utils";
interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
}
const overviewItems: NavItem[] = [
  { label: "Dashboard", href: "/", icon: LayoutDashboard },
];
const assetItems: NavItem[] = [
  { label: "Vehicles", href: "/vehicles", icon: Truck },
  { label: "Animals", href: "/animals", icon: Bug },
  { label: "Pastures", href: "/pastures", icon: Fence },
  { label: "Tools", href: "/tools", icon: Wrench },
  { label: "Gardens", href: "/gardens", icon: Flower2 },
  { label: "Implements", href: "/implements", icon: Cog },
];
const cropItems: NavItem[] = [
  { label: "Crops", href: "/crops", icon: Sprout },
  { label: "Plantings", href: "/plantings", icon: Sprout },
  { label: "Crop Planner", href: "/crops/planner", icon: CalendarRange },
];
const operationItems: NavItem[] = [
  { label: "Inventory", href: "/inventory", icon: Package },
  { label: "Contacts", href: "/contacts", icon: Users },
  { label: "Templates", href: "/templates", icon: FileBadge },
  { label: "Logs", href: "/logs", icon: ScrollText },
];
const documentItems: NavItem[] = [
  { label: "All Documents", href: "/documents", icon: FileText },
  { label: "Upload", href: "/documents/upload", icon: Upload },
  { label: "Taxonomy", href: "/taxonomy", icon: Tags },
];
function NavGroup({ label, items }: { label: string; items: NavItem[] }) {
  const location = useLocation();
  return (
    <SidebarGroup>
      <SidebarGroupLabel>{label}</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => {
          const isActive = item.href === "/"
            ? location.pathname === "/"
            : location.pathname.startsWith(item.href);
          return (
            <SidebarMenuItem key={item.href}>
              <SidebarMenuButton
                asChild
                isActive={isActive}
                className={cn(
                  isActive && "border-l-2 border-ranch-gold bg-accent"
                )}
              >
                <Link to={item.href}>
                  <item.icon className="size-4" />
                  <span>{item.label}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
export function AppSidebar(): JSX.Element {
  const userName = useAuthStore((s) => s.user?.name ?? "");
  const userEmail = useAuthStore((s) => s.user?.email ?? "");
  const ranchName = useAuthStore((s) => s.ranch?.name ?? "HollerHub");
  const handleLogout = async () => {
    await logout();
  };
  return (
    <Sidebar>
      <SidebarHeader>
        <div className="flex items-center gap-3 px-3 py-3">
          {/* E2 brand mark — outer frame + H inherit currentColor so they
              flip in dark mode. The Opaque Clay inner square is fixed. */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 64 64"
            className="h-9 w-9 flex-shrink-0 text-forest dark:text-forest-dark"
            role="img"
            aria-label="HollerHub"
          >
            <rect x="1.75" y="1.75" width="60.5" height="60.5" rx="6" ry="6"
                  fill="none" stroke="currentColor" strokeWidth="3.5" />
            <rect x="9.25" y="9.25" width="45.5" height="45.5" rx="3" ry="3"
                  fill="none" stroke="#B5603E" strokeOpacity="0.35" strokeWidth="1.5" />
            <g fill="currentColor">
              <rect x="18.5" y="18" width="6" height="28" />
              <rect x="39.5" y="18" width="6" height="28" />
              <rect x="18.5" y="29" width="27" height="6" />
            </g>
          </svg>
          <div className="flex flex-col leading-tight">
            <span className="font-display text-base text-forest dark:text-forest-dark">
              Holler<span className="italic text-clay dark:text-clay-dark">Hub</span>
            </span>
            <span className="font-mono text-2xs text-muted-foreground tracking-wide truncate">
              {ranchName}
            </span>
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <NavGroup label="Overview" items={overviewItems} />
        <SidebarSeparator />
        <NavGroup label="Assets" items={assetItems} />
        <SidebarSeparator />
        <NavGroup label="Crops" items={cropItems} />
        <SidebarSeparator />
        <NavGroup label="Operations" items={operationItems} />
        <SidebarSeparator />
        <NavGroup label="Documents" items={documentItems} />
      </SidebarContent>
      <SidebarFooter>
        <div className="flex items-center justify-between px-3 py-2">
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-medium text-foreground truncate">{userName}</span>
            <span className="text-2xs text-muted-foreground truncate">{userEmail}</span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <ThemeToggle className="text-muted-foreground hover:text-foreground" />
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Logout"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
